import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function KnowledgeManager() {
  const navigate = useNavigate();
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  const fetchKnowledgeBases = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/knowledge/list`);
      const data = await resp.json();
      if (data.success) {
        setKnowledgeBases(data.bases);
      }
    } catch (e) {
      console.error('获取知识库列表失败', e);
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    return (bytes / 1024 / 1024 / 1024).toFixed(1) + ' GB';
  };

  const getIcon = (name) => {
    const icons = {
      'ISM': '📚',
      'NCC': '📖',
      'YONBIP高级版': '📘',
      '运维': '⚙️',
      '热点Q&A': '🔥',
      '项目知识库': '📁'
    };
    return icons[name] || '📂';
  };

  const getColor = (name) => {
    const colors = {
      'ISM': '#1890ff',
      'NCC': '#52c41a',
      'YONBIP高级版': '#722ed1',
      '运维': '#fa8c16',
      '热点Q&A': '#ff4d4f',
      '项目知识库': '#13c2c2'
    };
    return colors[name] || '#8c8c8c';
  };

  if (loading) {
    return (
      <div className="knowledge-container">
        <div className="knowledge-loading">
          <div className="knowledge-spinner" />
          <p>加载知识库...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="knowledge-container">
      <div className="knowledge-header">
        <h2>📚 云端知识库管理</h2>
        <p className="knowledge-desc">管理和维护云端知识库，支持查看、搜索、导入导出</p>
      </div>

      <div className="knowledge-stats">
        <div className="knowledge-stat-card">
          <span className="stat-value">{knowledgeBases.length}</span>
          <span className="stat-label">知识库</span>
        </div>
        <div className="knowledge-stat-card">
          <span className="stat-value">{knowledgeBases.reduce((sum, b) => sum + (b.files || 0), 0)}</span>
          <span className="stat-label">文件总数</span>
        </div>
        <div className="knowledge-stat-card">
          <span className="stat-value">{formatSize(knowledgeBases.reduce((sum, b) => sum + (b.size || 0), 0))}</span>
          <span className="stat-label">总大小</span>
        </div>
      </div>

      <div className="knowledge-grid">
        {knowledgeBases.map((kb) => (
          <div 
            key={kb.name} 
            className="knowledge-card"
            style={{ '--kb-color': getColor(kb.name) }}
          >
            <div className="kb-icon">{getIcon(kb.name)}</div>
            <div className="kb-info">
              <h3>{kb.name}</h3>
              <div className="kb-meta">
                <span>{formatSize(kb.size)}</span>
                <span>•</span>
                <span>{kb.files} 文件</span>
              </div>
              {kb.updated && (
                <div className="kb-updated">更新于 {kb.updated}</div>
              )}
            </div>
            <div className="kb-actions">
              <button 
                className="kb-btn kb-btn-primary"
                onClick={() => navigate(`/knowledge/browse/${encodeURIComponent(kb.name)}`)}
              >
                {kb.isManual ? '📖 手册' : '查看'}
              </button>
              {kb.name === 'ISM' && localStorage.getItem('notes_user') === 'jch' && (
                <button 
                  className="kb-btn kb-btn-special"
                  onClick={() => navigate('/knowledge/ism')}
                >
                  提取
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="knowledge-features">
        <h3>功能入口</h3>
        <div className="feature-grid">
          {/* 强调功能 - 智能问答 */}
          <div className="feature-card featured" onClick={() => navigate('/knowledge/qa')}>
            <span className="feature-icon">🤖</span>
            <span className="feature-title">智能问答</span>
            <span className="feature-desc">基于知识库智能回答用友产品问题</span>
          </div>
          {/* 强调功能 - 日志分析 */}
          <div className="feature-card featured" onClick={() => navigate('/knowledge/log-analysis')}>
            <span className="feature-icon">📋</span>
            <span className="feature-title">日志分析</span>
            <span className="feature-desc">上传SPR日志智能分析错误</span>
          </div>
          {/* 普通功能 */}
          <div className="feature-card" onClick={() => navigate('/knowledge/search')}>
            <span className="feature-icon">🔍</span>
            <span className="feature-title">全局搜索</span>
            <span className="feature-desc">跨知识库搜索知识点</span>
          </div>
          <div className="feature-card" onClick={() => navigate('/knowledge/dictionary')}>
            <span className="feature-icon">🗃️</span>
            <span className="feature-title">数据字典</span>
            <span className="feature-desc">查询NCC表结构和SQL生成</span>
          </div>
          {localStorage.getItem('notes_user') === 'jch' && (
          <div className="feature-card" onClick={() => navigate('/knowledge/ism')}>
            <span className="feature-icon">📥</span>
            <span className="feature-title">ISM知识提取</span>
            <span className="feature-desc">从用友ISM平台提取知识条目</span>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
